import React, { useState } from 'react';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../config/api';
import { parsePhoneNumber } from '../../utils/phoneNumberParser';
import { isValidStateAbbreviation } from '../../utils/stateValidator';

/**
 * CustomerFloatingForm - Customer creation form for floating window
 */
export default function CustomerFloatingForm({ onSubmit, onCancel }) {
  const [error, setError] = useState(null);
  const { apiCall } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    driver_license_number: '',
    driver_license_state: 'IN',
    international_license: false,
    referral: '',
    sms_consent: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'driver_license_state') {
      setFormData(prev => ({ ...prev, [name]: value.slice(0, 2).toUpperCase() }));
    } else if (name === 'international_license') {
      setFormData(prev => ({ ...prev, [name]: value === 'yes' }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    let parsedPhone;
    try {
      parsedPhone = parsePhoneNumber(formData.phone_number);
    } catch (err) {
      setError(err.message);
      return;
    }

    if (!isValidStateAbbreviation(formData.driver_license_state)) {
      setError('Please enter a valid US state abbreviation (2 characters)');
      return;
    }

    try {
      const { sms_consent, ...customerFields } = { ...formData, phone_number: parsedPhone };

      const response = await apiCall(`${API_BASE_URL}/customers`, {
        method: 'POST',
        body: JSON.stringify(customerFields),
      });

      const newCustomer = response.data || response;

      if (sms_consent && newCustomer.id) {
        try {
          await apiCall(`${API_BASE_URL}/customers/${newCustomer.id}/consent`, {
            method: 'POST',
            body: JSON.stringify({ consent_given: true }),
          });
        } catch (consentErr) {
          console.error('Failed to record SMS consent:', consentErr);
        }
      }

      onSubmit?.();
    } catch (err) {
      setError(err.message || 'Failed to create customer');
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Row>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="first_name">
            <Form.Label className="mb-1 small">First Name</Form.Label>
            <Form.Control size="sm" name="first_name" value={formData.first_name} onChange={handleChange} required />
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="last_name">
            <Form.Label className="mb-1 small">Last Name</Form.Label>
            <Form.Control size="sm" name="last_name" value={formData.last_name} onChange={handleChange} required />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="email">
            <Form.Label className="mb-1 small">Email</Form.Label>
            <Form.Control size="sm" type="email" name="email" value={formData.email} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="phone_number">
            <Form.Label className="mb-1 small">Phone Number</Form.Label>
            <Form.Control size="sm" type="tel" name="phone_number" value={formData.phone_number} onChange={handleChange} required />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="driver_license_number">
            <Form.Label className="mb-1 small">License Number</Form.Label>
            <Form.Control size="sm" name="driver_license_number" value={formData.driver_license_number} onChange={handleChange} required />
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="driver_license_state">
            <Form.Label className="mb-1 small">License State</Form.Label>
            <Form.Control size="sm" name="driver_license_state" value={formData.driver_license_state} onChange={handleChange} required maxLength="2" />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="international_license">
            <Form.Label className="mb-1 small">International License</Form.Label>
            <Form.Select size="sm" name="international_license" value={formData.international_license ? 'yes' : 'no'} onChange={handleChange}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={6}>
          <Form.Group className="mb-2" controlId="referral">
            <Form.Label className="mb-1 small">Referral</Form.Label>
            <Form.Control size="sm" name="referral" value={formData.referral} onChange={handleChange} />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-2">
        <Form.Check
          type="checkbox"
          id="fab_sms_consent"
          name="sms_consent"
          label="Customer consents to receive SMS notifications"
          checked={formData.sms_consent}
          onChange={handleChange}
        />
        <Form.Text className="text-muted small">
          Message frequency varies. Message and data rates may apply.
        </Form.Text>
      </Form.Group>

      <div className="d-flex gap-2 mt-2">
        <Button type="submit" variant="primary" size="sm">Create Customer</Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </Form>
  );
}
